import React, { useMemo, useState, useCallback, useEffect } from "react";
import "./YouTubeFacade.css";
import { useTranslation } from "react-i18next";

export default function YouTubeFacade({
  videoId,
  videoUrl,
  title = "YouTube Video",
  className = "",
}) {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);

  const extractYouTubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const resolvedVideoId = useMemo(() => {
    if (videoId) return videoId;
    return extractYouTubeId(videoUrl);
  }, [videoId, videoUrl]);

  const thumbnailUrl = useMemo(() => {
    if (!resolvedVideoId) return "";
    return `https://i.ytimg.com/vi/${resolvedVideoId}/hqdefault.jpg`;
  }, [resolvedVideoId]);

  const iframeUrl = useMemo(() => {
    if (!resolvedVideoId) return "";
    return `https://www.youtube-nocookie.com/embed/${resolvedVideoId}?autoplay=1&rel=0&modestbranding=1`;
  }, [resolvedVideoId]);

  const checkFunctionalConsent = useCallback(() => {
  try {
    const stored = localStorage.getItem("consentmode");
    if (!stored) return false;

    const parsed = JSON.parse(stored);

    return (
      parsed &&
      typeof parsed === "object" &&
      parsed.functionality_storage === "granted"
    );
  } catch (e) {
    return false;
  }
}, []);

  useEffect(() => {
  const updateConsent = () => {
    setHasConsent(checkFunctionalConsent());
  };

  updateConsent();

  window.addEventListener("CookiebotOnConsentReady", updateConsent);
  window.addEventListener("CookiebotOnAccept", updateConsent);
  window.addEventListener("CookiebotOnDecline", updateConsent);
  window.addEventListener("storage", updateConsent);

  return () => {
    window.removeEventListener("CookiebotOnConsentReady", updateConsent);
    window.removeEventListener("CookiebotOnAccept", updateConsent);
    window.removeEventListener("CookiebotOnDecline", updateConsent);
    window.removeEventListener("storage", updateConsent);
  };
}, [checkFunctionalConsent]);

  
  const handlePlay = useCallback((e) => {
  e.preventDefault();
  e.stopPropagation();

  const consentGranted = checkFunctionalConsent();
  setHasConsent(consentGranted);

  if (consentGranted) {
    setIsPlaying(true);
    return;
  }

  setIsPlaying(false);

  window.dispatchEvent(
    new CustomEvent("open-cookie-settings", {
      detail: { requestedCategory: "functional" },
    })
  );
}, [checkFunctionalConsent]);

  if (!resolvedVideoId) {
    return (
      <div className={`yt-facade yt-facade--empty ${className}`}>
        <span>{t("footer.Video not available")}</span>
      </div>
    );
  }

  return (
    <div className={`yt-facade ${className}`}>
      {!isPlaying ? (
        <button
          className="yt-facade__button"
          onClick={handlePlay}
          aria-label={`Play video ${title}`}
        >
          <img
            src={thumbnailUrl}
            alt={`Thumbnail for ${title}`}
            className="yt-facade__thumb"
            loading="lazy"
          />
          <div className="yt-facade__overlay"></div>

          <div className="yt-facade__play">
            <svg viewBox="0 0 68 48" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55c-2.93.78-4.63 3.26-5.42 6.19C.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z"
                fill="red"
              />
              <path d="M45 24L27 14v20" fill="white" />
            </svg>
          </div>

          {!hasConsent && (
            <div className="yt-facade__warning">
              {t("footer.Please accept Functional cookies to play this video.")}
            </div>
          )}
        </button>
      ) : (
        <iframe
          width="100%"
          height="100%"
          src={iframeUrl}
          title={title}
          style={{ border: 0 }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      )}
    </div>
  );
}